import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { ChevronDown, ExternalLink } from "lucide-react";
import { ALL_PAGES } from "../../pages/queries";
import { GET_NAV_CONFIG } from "../../navigation/queries";

interface LinkFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** When provided, shows an "Open in new tab" toggle. */
  target?: string;
  /** Callback for target changes. Only meaningful when target is provided. */
  onTargetChange?: (target: string) => void;
}

/** Built-in feature paths available to all tenants. */
const BUILT_IN_FEATURES = [
  { path: "/book", label: "Book a session" },
  { path: "/support", label: "Support" },
  { path: "/faq", label: "FAQ" },
  { path: "/about", label: "About" },
  { path: "/pricing", label: "Pricing" },
  { path: "/legal-insights", label: "Blog" },
] as const;

interface FeatureOption {
  path: string;
  label: string;
}

interface PageOption {
  slug: string;
  title: string | null;
}

/**
 * Link picker for button/CTA link props.
 *
 * Shows a dropdown with tenant pages, built-in feature paths,
 * and an "External URL" option for custom links.
 *
 * Stores the path string as the prop value (same schema as before).
 */
export function LinkField({ label, value, onChange, target, onTargetChange }: LinkFieldProps) {
  const [isCustom, setIsCustom] = useState(false);

  // Fetch tenant pages
  const { data: pagesData } = useQuery(ALL_PAGES, {
    variables: { page: 0, perPage: 200 },
  });

  // Fetch tenant feature config for custom feature paths
  const { data: tenantData } = useQuery(GET_NAV_CONFIG);

  const pages: PageOption[] = useMemo(() => {
    const all = pagesData?.allPages ?? [];
    return all
      .filter((p: { status: string }) => p.status !== "archived")
      .map((p: { slug: string; title: string | null }) => ({
        slug: p.slug,
        title: p.title,
      }));
  }, [pagesData]);

  // Derive features: prefer tenant's featureConfig if available,
  // fall back to static list
  const features: FeatureOption[] = useMemo(() => {
    const raw = tenantData?.currentTenant?.featureConfig;
    if (!raw) return [...BUILT_IN_FEATURES];
    try {
      const parsed = JSON.parse(raw as string);
      const entries: FeatureOption[] = [];
      for (const [key, val] of Object.entries(parsed)) {
        const v = val as { enabled?: boolean; path?: string };
        if (v.enabled === false) continue;
        const builtIn = BUILT_IN_FEATURES.find(
          (f) => f.path === (v.path ?? `/${key}`),
        );
        entries.push({
          path: v.path ?? `/${key}`,
          label: builtIn?.label ?? key,
        });
      }
      // Include any built-in features not in the config
      for (const f of BUILT_IN_FEATURES) {
        if (!entries.some((e) => e.path === f.path)) {
          entries.push(f);
        }
      }
      return entries;
    } catch {
      return [...BUILT_IN_FEATURES];
    }
  }, [tenantData]);

  // Determine what the current value matches
  const matchesPage = pages.find((p) => `/${p.slug}` === value || (p.slug === "" && value === "/"));
  const matchesFeature = features.find((f) => f.path === value);
  const isExternal =
    isCustom ||
    (value !== "" && !matchesPage && !matchesFeature);

  // Derive the select value for controlled component
  const selectValue = matchesPage
    ? `page:${matchesPage.slug}`
    : matchesFeature
      ? `feature:${matchesFeature.path}`
      : isExternal
        ? "external"
        : "";

  return (
    <div className="mb-[18px]">
      <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
        {label}
      </label>

      {/* Link type selector */}
      <div className="relative">
        <select
          value={selectValue}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "external") {
              setIsCustom(true);
              // Keep existing value if it looks external, else clear
              if (matchesPage || matchesFeature) {
                onChange("");
              }
            } else if (v.startsWith("page:")) {
              setIsCustom(false);
              const slug = v.slice(5);
              onChange(slug === "" ? "/" : `/${slug}`);
            } else if (v.startsWith("feature:")) {
              setIsCustom(false);
              onChange(v.slice(8));
            } else {
              setIsCustom(false);
              onChange("");
            }
          }}
          className="w-full appearance-none border border-ink-6 rounded-[9px] bg-paper pl-[11px] pr-8 py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all cursor-pointer"
        >
          <option value="">Select a link...</option>

          {/* Tenant pages */}
          {pages.length > 0 && (
            <optgroup label="Pages">
              {pages.map((p) => (
                <option key={`page:${p.slug}`} value={`page:${p.slug}`}>
                  {p.title || `/${p.slug || "/"}`}
                </option>
              ))}
            </optgroup>
          )}

          {/* Feature paths */}
          <optgroup label="Built-in pages">
            {features.map((f) => (
              <option key={`feature:${f.path}`} value={`feature:${f.path}`}>
                {f.label} ({f.path})
              </option>
            ))}
          </optgroup>

          <option value="external">External URL...</option>
        </select>

        <ChevronDown
          size={13}
          strokeWidth={2}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-5 pointer-events-none"
        />
      </div>

      {/* External URL text input */}
      {isExternal && (
        <div className="mt-2 flex items-center gap-1.5">
          <ExternalLink
            size={12}
            strokeWidth={2}
            className="flex-none text-ink-5"
          />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://example.com"
            className="flex-1 border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 font-mono text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
          />
        </div>
      )}

      {/* Show resolved path */}
      {value && !isExternal && (
        <p className="mt-1.5 text-[10.5px] text-ink-5 font-mono">
          {value}
        </p>
      )}

      {/* Open in new tab toggle */}
      {target !== undefined && onTargetChange && (
        <label className="flex items-center gap-2.5 cursor-pointer group mt-2">
          <input
            type="checkbox"
            checked={target === "_blank"}
            onChange={(e) =>
              onTargetChange(e.target.checked ? "_blank" : "_self")
            }
            className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer"
          />
          <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors">
            Open in new tab
          </span>
        </label>
      )}
    </div>
  );
}
